import React, { useEffect, useState } from 'react';
import { Palette, Save, RefreshCw, Loader2, Eye, EyeOff, Layout, Type, Sparkles, Undo2, ArrowUpDown, ArrowUp, ArrowDown, GripVertical, LayoutGrid, Percent, Plus, Trash2, Building2, Bike, Package, Truck, Leaf, Utensils, ShoppingBasket, Coffee, Gift, Heart, Store, Wrench, Wine, Home, Baby, Book, Camera, Music, History, PlusSquare, Move, Megaphone, Image as ImageIcon, MessageSquare, Youtube, Bot } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { publicSettings } from '../services/publicSettings';

// The full list of landing-page + brand tokens the admin can edit here. Each
// section owns a subset of `platform_settings` keys — the backend's
// PUBLIC_KEYS whitelist keeps them safe to expose to the browser.
const SECTIONS = [
  {
    id: 'hero', label: 'Landing hero', icon: Type,
    description: 'Headline, tagline and mission text shown at the top of the home page.',
    fields: [
      { key: 'landing_hero_title', label: 'Hero title', type: 'text', placeholder: 'ShipitNow' },
      { key: 'landing_hero_tagline', label: 'Tagline', type: 'text', placeholder: 'Anything · Anytime · Delivered' },
      { key: 'landing_hero_mission', label: 'Mission statement', type: 'textarea',
        placeholder: 'ShipitNow uses its registered and approved drivers to deliver…' },
    ],
  },
  {
    id: 'labels', label: 'Page labels', icon: Type,
    description: 'Short strings sprinkled across the home page — search prompt, section headings, etc.',
    fields: [
      { key: 'landing_search_placeholder', label: 'Search bar placeholder', type: 'text', placeholder: 'Search businesses, products, services…' },
      { key: 'landing_vendor_heading', label: 'Vendor list heading', type: 'text', placeholder: 'What can we deliver for you today?' },
      { key: 'landing_modes_title', label: 'Modes ribbon title', type: 'text', placeholder: 'All Modes · One Promise' },
    ],
  },
  {
    id: 'sections', label: 'Page sections', icon: Layout,
    description: 'Toggle major blocks on the landing page on or off.',
    fields: [
      { key: 'landing_show_service_tiles', label: 'Service tiles (Food, Grocery, Courier…)', type: 'toggle' },
      { key: 'landing_show_modes_ribbon', label: 'Modes ribbon (Road, Air, Sea, Rail, Bike)', type: 'toggle' },
      { key: 'landing_show_promise_ribbon', label: 'Promise ribbon (Fast & Reliable, Safe & Secure…)', type: 'toggle' },
    ],
  },
  {
    id: 'theme', label: 'Brand theme', icon: Palette,
    description: 'Primary color, accent color, font family, and tile density applied across the app.',
    fields: [
      { key: 'brand_primary_color', label: 'Primary color', type: 'color', placeholder: '#1E4FA6' },
      { key: 'brand_accent_color', label: 'Accent color', type: 'color', placeholder: '#F1671B' },
      { key: 'brand_font_family', label: 'Font family', type: 'select',
        options: [
          { value: 'system', label: 'System (default)' },
          { value: 'Inter', label: 'Inter — modern SaaS' },
          { value: 'Poppins', label: 'Poppins — friendly & bold' },
          { value: 'Roboto', label: 'Roboto — Google native' },
        ] },
      { key: 'landing_tile_size', label: 'Service tile size', type: 'select',
        options: [
          { value: 'compact', label: 'Compact — smaller padding & icons' },
          { value: 'default', label: 'Default (recommended)' },
          { value: 'roomy', label: 'Roomy — larger tiles & bolder icons' },
        ] },
    ],
  },
  {
    id: 'custom_sections', label: 'Custom sections', icon: PlusSquare,
    description: 'Add totally new landing blocks — headline banners, image rows, testimonials, or video embeds.',
    fields: [{ key: 'landing_custom_sections', label: 'Sections', type: 'custom_sections' }],
  },
  {
    id: 'layout_boxes', label: 'Per-box size', icon: Move,
    description: 'Fine-tune the width and padding of each landing section. Sliders apply live in the preview.',
    fields: [{ key: 'landing_section_layout', label: 'Section layout', type: 'section_layout' }],
  },
  {
    id: 'tiles', label: 'Service tiles', icon: LayoutGrid,
    description: 'Add, rename, reorder, hide or delete individual service tiles on the landing page.',
    fields: [{ key: 'landing_service_tiles', label: 'Tiles', type: 'tiles' }],
  },
  {
    id: 'ai_providers', label: 'AI providers', icon: Bot,
    description: 'Route each AI feature to OpenAI (default), Gemini or Claude. All models run through the Emergent LLM key — no extra API keys needed.',
    fields: [
      { key: 'ai_provider_default', label: 'Global default', type: 'select',
        options: [
          { value: 'openai', label: 'OpenAI · gpt-5.2 (default)' },
          { value: 'openai_pro', label: 'OpenAI · gpt-5.4 (Pro)' },
          { value: 'gemini_pro', label: 'Gemini · 3.1 Pro (reasoning)' },
          { value: 'gemini_flash', label: 'Gemini · 3 Flash (fast/cheap)' },
          { value: 'claude_sonnet', label: 'Claude · Sonnet 5 (balanced)' },
          { value: 'claude_opus', label: 'Claude · Opus 4.8 (deep reasoning)' },
          { value: 'claude_haiku', label: 'Claude · Haiku 4.5 (fast/cheap)' },
        ] },
      { key: 'ai_provider_menu_description', label: 'Menu-item description writer', type: 'select',
        options: [
          { value: '__default', label: 'Use global default' },
          { value: 'openai', label: 'OpenAI · gpt-5.2' },
          { value: 'openai_pro', label: 'OpenAI · gpt-5.4 (Pro)' },
          { value: 'gemini_pro', label: 'Gemini · 3.1 Pro' },
          { value: 'gemini_flash', label: 'Gemini · 3 Flash' },
          { value: 'claude_sonnet', label: 'Claude · Sonnet 5' },
          { value: 'claude_haiku', label: 'Claude · Haiku 4.5 (recommended)' },
        ] },
      { key: 'ai_provider_onboarding_chat', label: 'Vendor onboarding chat', type: 'select',
        options: [
          { value: '__default', label: 'Use global default' },
          { value: 'openai', label: 'OpenAI · gpt-5.2' },
          { value: 'openai_pro', label: 'OpenAI · gpt-5.4 (Pro)' },
          { value: 'gemini_pro', label: 'Gemini · 3.1 Pro' },
          { value: 'gemini_flash', label: 'Gemini · 3 Flash' },
          { value: 'claude_sonnet', label: 'Claude · Sonnet 5 (recommended)' },
          { value: 'claude_opus', label: 'Claude · Opus 4.8' },
        ] },
      { key: 'ai_provider_invoice_parsing', label: 'Invoice parsing (Vision)', type: 'select',
        options: [
          { value: '__default', label: 'Use global default' },
          { value: 'openai', label: 'OpenAI · gpt-5.2 Vision' },
          { value: 'gemini_pro', label: 'Gemini · 3.1 Pro Vision (recommended)' },
          { value: 'gemini_flash', label: 'Gemini · 3 Flash Vision' },
          { value: 'claude_sonnet', label: 'Claude · Sonnet 5 Vision' },
          { value: 'claude_opus', label: 'Claude · Opus 4.8 Vision' },
        ] },
      { key: 'ai_provider_pod_verification', label: 'POD photo verification', type: 'select',
        options: [
          { value: '__default', label: 'Use global default' },
          { value: 'openai', label: 'OpenAI · gpt-5.2 Vision' },
          { value: 'gemini_flash', label: 'Gemini · 3 Flash Vision (recommended)' },
          { value: 'claude_haiku', label: 'Claude · Haiku 4.5 Vision' },
        ] },
      { key: 'ai_provider_support_chatbot', label: 'Storefront support chatbot', type: 'select',
        options: [
          { value: '__default', label: 'Use global default' },
          { value: 'openai', label: 'OpenAI · gpt-5.2' },
          { value: 'gemini_flash', label: 'Gemini · 3 Flash (recommended)' },
          { value: 'gemini_pro', label: 'Gemini · 3.1 Pro' },
          { value: 'claude_haiku', label: 'Claude · Haiku 4.5' },
          { value: 'claude_sonnet', label: 'Claude · Sonnet 5' },
        ] },
      { key: 'ai_provider_driver_matching', label: 'Driver matching hints', type: 'select',
        options: [
          { value: '__default', label: 'Use global default' },
          { value: 'openai', label: 'OpenAI · gpt-5.2' },
          { value: 'gemini_flash', label: 'Gemini · 3 Flash (recommended)' },
          { value: 'claude_haiku', label: 'Claude · Haiku 4.5' },
        ] },
    ],
  },
  {
    id: 'taxes', label: 'City tax rates', icon: Percent,
    description: 'Auto-applied when a vendor has not set their own tax_rate. Ontario HST is 13%, Ibadan VAT is 7.5%.',
    fields: [{ key: 'city_default_tax_rates', label: 'City tax rates', type: 'city_tax' }],
  },
  {
    id: 'order', label: 'Layout order', icon: ArrowUpDown,
    description: 'Drag or use the arrows to reorder landing sections. The order applies below the hero.',
    fields: [{ key: 'landing_section_order', label: 'Section order', type: 'reorder' }],
  },
];

// Static defaults mirror settings_routes.DEFAULT_SETTINGS so the Reset button
// can restore instantly without a network round-trip.
const DEFAULTS = {
  landing_hero_title: 'ShipitNow',
  landing_hero_tagline: 'Anything · Anytime · Delivered',
  landing_hero_mission: 'ShipItNow uses its registered and approved drivers to deliver food, same-day delivery, courier services, and business logistics in Canada and Ibadan.',
  landing_show_service_tiles: true,
  landing_show_modes_ribbon: true,
  landing_show_promise_ribbon: true,
  landing_section_order: ['service_tiles', 'modes_ribbon', 'promise_ribbon'],
  landing_search_placeholder: 'Search businesses, products, services…',
  landing_vendor_heading: 'What can we deliver for you today?',
  landing_modes_title: 'All Modes · One Promise',
  landing_tile_size: 'default',
  landing_service_tiles: [
    { id: 'food', name: 'Food Delivery', icon: 'Utensils', color: '#F97316', category: 'food', query: '', hidden: false },
    { id: 'grocery', name: 'African Grocery', icon: 'ShoppingBasket', color: '#059669', category: 'grocery', query: '', hidden: false },
    { id: 'farm_produce', name: 'Farm Produce', icon: 'Leaf', color: '#16A34A', category: 'farm_produce', query: '', hidden: false },
    { id: 'courier', name: 'Courier Services', icon: 'Bike', color: '#0EA5E9', category: 'courier', query: '', hidden: false },
    { id: 'same_day', name: 'Same-Day Package', icon: 'Package', color: '#8B5CF6', category: 'package', query: '', hidden: false },
    { id: 'business_logistics', name: 'Business Logistics', icon: 'Building2', color: '#0369A1', category: 'logistics', query: '', hidden: false },
  ],
  city_default_tax_rates: { Ottawa: 13.0, Ibadan: 7.5 },
  landing_custom_sections: [],
  landing_section_layout: {},
  brand_primary_color: '#1E4FA6',
  brand_accent_color: '#F1671B',
  brand_font_family: 'system',
};

const ConfigurationPage = () => {
  const { user } = useAuth();
  const isOAuth = user?.provider === 'google';
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [confirm, setConfirm] = useState({ open: false, password: '' });
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sectionId, setSectionId] = useState('hero');

  const load = async () => {
    try {
      const r = await api.get('/admin/settings');
      setData(r.data);
      setDraft({});
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load settings'); }
  };
  useEffect(() => { load(); }, []);

  const currentValue = (k) => (k in draft ? draft[k] : (data?.[k] ?? DEFAULTS[k]));
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const isDirty = Object.keys(draft).length > 0;

  const resetSection = () => {
    const section = SECTIONS.find((s) => s.id === sectionId);
    if (!section) return;
    if (!window.confirm(`Reset all ${section.label.toLowerCase()} fields to their defaults?`)) return;
    setDraft((d) => {
      const next = { ...d };
      section.fields.forEach((f) => { next[f.key] = DEFAULTS[f.key]; });
      return next;
    });
    toast.success(`${section.label} reset — click Save to apply`);
  };

  const askSave = () => {
    if (!isDirty) { toast.error('No changes to save'); return; }
    setConfirm({ open: true, password: '' });
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const pwd = isOAuth && !confirm.password ? 'I_CONFIRM' : confirm.password;
      await api.patch('/admin/settings', { updates: draft, password: pwd });
      publicSettings.reset();
      toast.success('Configuration saved — refresh the home page to see changes');
      setConfirm({ open: false, password: '' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const r = await api.get('/admin/settings/history');
      setHistoryItems(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  const restoreSnapshot = async (snap) => {
    if (!window.confirm(`Restore configuration from ${new Date(snap.saved_at).toLocaleString()}?\n\nThis will replace ${snap.changed_keys?.length || 0} setting${snap.changed_keys?.length === 1 ? '' : 's'} and create a new snapshot so you can undo the restore itself.`)) return;
    // Reuse the same password gating as regular save.
    let pwd = 'I_CONFIRM';
    if (!isOAuth) {
      const entered = window.prompt('Re-enter your admin password to restore:');
      if (!entered) return;
      pwd = entered;
    }
    try {
      const r = await api.post(`/admin/settings/history/${snap.id}/restore`, { updates: {}, password: pwd });
      publicSettings.reset();
      toast.success(`Restored ${r.data?.keys?.length || 0} settings — refresh the home page`);
      setHistoryOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
  };

  const section = SECTIONS.find((s) => s.id === sectionId);

  return (
    <div className="pb-16">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-indigo-500" />
            Configuration portal
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl">
            Edit landing-page copy, toggle sections on and off, and tune the brand palette. Changes go live immediately after Save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openHistory} data-testid="config-history">
            <History className="h-4 w-4 mr-1.5" />History
          </Button>
          <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="config-preview">
            <Eye className="h-4 w-4 mr-1.5" />Preview
          </Button>
          <Button variant="outline" onClick={load} data-testid="config-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" />Reload
          </Button>
          <Button
            onClick={askSave}
            disabled={!isDirty || saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-300 disabled:hover:bg-slate-300"
            data-testid="config-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save changes</>}
          </Button>
        </div>
      </div>

      {isDirty && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-2.5 text-sm flex items-center gap-2" data-testid="config-dirty-banner">
          <span className="font-semibold">{Object.keys(draft).length} unsaved change{Object.keys(draft).length === 1 ? '' : 's'}</span>
          <span className="text-amber-700">— click Save to apply, or Reload to discard.</span>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Section rail */}
        <nav className="lg:col-span-1 rounded-2xl border border-slate-200 bg-white p-2 h-fit sticky top-4" data-testid="config-sections">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = s.id === sectionId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSectionId(s.id)}
                data-testid={`config-section-${s.id}`}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center gap-2 transition ${active ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{s.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Section content */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5">
          {!section ? null : (
            <>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{section.label}</h2>
                  <p className="text-slate-500 text-sm mt-0.5">{section.description}</p>
                </div>
                <Button variant="outline" size="sm" onClick={resetSection} data-testid="config-section-reset">
                  <Undo2 className="h-3.5 w-3.5 mr-1" />Reset to defaults
                </Button>
              </div>

              <div className="mt-5 space-y-4" data-testid={`config-fields-${section.id}`}>
                {section.fields.map((f) => (
                  <FieldRow
                    key={f.key}
                    field={f}
                    value={currentValue(f.key)}
                    dirty={f.key in draft}
                    onChange={(v) => setField(f.key, v)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Sticky live preview — only shown on wide screens. Reads directly
            from draft state so it auto-updates as the admin edits. */}
        <aside className="hidden lg:block lg:col-span-2" data-testid="config-live-preview">
          <div className="sticky top-4 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" /> Live preview
              {isDirty && <span className="text-amber-700 normal-case tracking-normal font-normal">— reflects your unsaved edits</span>}
            </div>
            <HeroPreview
              title={currentValue('landing_hero_title')}
              tagline={currentValue('landing_hero_tagline')}
              mission={currentValue('landing_hero_mission')}
              primary={currentValue('brand_primary_color')}
              accent={currentValue('brand_accent_color')}
              showServiceTiles={currentValue('landing_show_service_tiles')}
              showModes={currentValue('landing_show_modes_ribbon')}
              showPromise={currentValue('landing_show_promise_ribbon')}
              tiles={currentValue('landing_service_tiles')}
              modesTitle={currentValue('landing_modes_title')}
              tileSize={currentValue('landing_tile_size')}
              vendorHeading={currentValue('landing_vendor_heading')}
            />
          </div>
        </aside>
      </div>

      {/* Password confirm dialog */}
      <Dialog open={confirm.open} onOpenChange={(o) => setConfirm((c) => ({ ...c, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm changes</DialogTitle>
            <DialogDescription>Password re-confirmation is required to update platform-wide settings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-600">
            <div>You&apos;re about to update {Object.keys(draft).length} configuration value{Object.keys(draft).length === 1 ? '' : 's'} that will affect every visitor.</div>
            {!isOAuth ? (
              <>
                <Label>Re-enter your admin password</Label>
                <Input type="password" value={confirm.password} onChange={(e) => setConfirm((c) => ({ ...c, password: e.target.value }))} autoFocus data-testid="config-confirm-password" />
              </>
            ) : (
              <div className="text-xs text-slate-500">Google-signed-in admins skip password re-confirmation.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm({ open: false, password: '' })}>Cancel</Button>
            <Button
              onClick={doSave}
              disabled={saving || (!isOAuth && !confirm.password)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="config-confirm-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Undo history */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configuration history</DialogTitle>
            <DialogDescription>The last 20 saves. Click Restore to roll back — the restore itself is snapshotted too, so it&apos;s always undoable.</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
          ) : historyItems.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-8">No prior saves yet.</div>
          ) : (
            <div className="space-y-2" data-testid="config-history-list">
              {historyItems.map((h) => (
                <div key={h.id} className="border border-slate-200 rounded-lg px-3 py-2.5 flex items-start gap-3" data-testid={`config-history-row-${h.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{new Date(h.saved_at).toLocaleString()}</div>
                    <div className="text-[11px] text-slate-500 truncate">by {h.saved_by_name} · {h.changed_keys?.length || 0} key(s) changed</div>
                    {h.diff_summary?.length > 0 && (
                      <div className="mt-1 text-[11px] text-slate-600 flex flex-wrap gap-1">
                        {h.diff_summary.slice(0, 6).map((d, i) => (
                          <span key={i} className="bg-slate-100 rounded px-1.5 py-0.5 font-mono">{d.key}</span>
                        ))}
                        {h.diff_summary.length > 6 && <span className="text-slate-400">+ {h.diff_summary.length - 6} more</span>}
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => restoreSnapshot(h)} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`config-history-restore-${h.id}`}>
                    <Undo2 className="h-3.5 w-3.5 mr-1" />Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Live preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
            <DialogDescription>Live preview of the landing hero with your unsaved edits applied.</DialogDescription>
          </DialogHeader>
          <HeroPreview
            title={currentValue('landing_hero_title')}
            tagline={currentValue('landing_hero_tagline')}
            mission={currentValue('landing_hero_mission')}
            primary={currentValue('brand_primary_color')}
            accent={currentValue('brand_accent_color')}
            showServiceTiles={currentValue('landing_show_service_tiles')}
            showModes={currentValue('landing_show_modes_ribbon')}
            showPromise={currentValue('landing_show_promise_ribbon')}
            tiles={currentValue('landing_service_tiles')}
            modesTitle={currentValue('landing_modes_title')}
            tileSize={currentValue('landing_tile_size')}
            vendorHeading={currentValue('landing_vendor_heading')}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FieldRow = ({ field, value, dirty, onChange }) => {
  const [showPickerHint, setShowPickerHint] = useState(false);
  const testid = `config-field-${field.key}`;
  if (field.type === 'toggle') {
    const on = Boolean(value);
    return (
      <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
        <div>
          <div className="text-sm font-medium text-slate-800">{field.label}</div>
          <div className="text-[11px] text-slate-400">{field.key}</div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge>}
          <button
            type="button"
            onClick={() => onChange(!on)}
            className={`relative h-6 w-11 rounded-full transition ${on ? 'bg-emerald-600' : 'bg-slate-300'}`}
            role="switch"
            aria-checked={on}
            data-testid={`${testid}-switch`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${on ? 'left-5' : 'left-0.5'}`} />
          </button>
        </div>
      </div>
    );
  }
  if (field.type === 'color') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
        <div>
          <div className="text-sm font-medium text-slate-800">{field.label}</div>
          <div className="text-[11px] text-slate-400">{field.key}</div>
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <input
            type="color"
            value={String(value || field.placeholder || '#000000')}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-14 rounded border border-slate-200 cursor-pointer"
            data-testid={`${testid}-picker`}
          />
          <Input
            value={String(value || '')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className="font-mono h-9 w-32"
            data-testid={`${testid}-hex`}
          />
          {dirty && <Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge>}
        </div>
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
        <div>
          <div className="text-sm font-medium text-slate-800">{field.label}</div>
          <div className="text-[11px] text-slate-400">{field.key}</div>
        </div>
        <div className="sm:col-span-2">
          <textarea
            value={String(value || '')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid={`${testid}-input`}
          />
          {dirty && <div className="mt-1"><Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge></div>}
        </div>
      </div>
    );
  }
  if (field.type === 'select') {
    // Sentinel-safe: shadcn Select rejects "" so we render an unset field as
    // '__default' when that option exists, otherwise as the first option.
    const opts = field.options || [];
    const hasDefault = opts.some((o) => o.value === '__default');
    const rawFallback = hasDefault ? '__default' : (opts[0]?.value ?? '');
    const raw = value === '' || value == null ? rawFallback : String(value);
    const selected = opts.find((o) => o.value === raw);
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
        <div>
          <div className="text-sm font-medium text-slate-800">{field.label}</div>
          <div className="text-[11px] text-slate-400">{field.key}</div>
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <Select value={raw} onValueChange={(v) => onChange(v === '__default' ? '' : v)}>
            <SelectTrigger
              className="h-9 w-64"
              data-testid={`${testid}-select`}
              style={field.key === 'brand_font_family' && value !== 'system' ? { fontFamily: `${value}, system-ui` } : undefined}
            >
              <SelectValue>{selected?.label || raw}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((o) => (
                <SelectItem
                  key={o.value}
                  value={o.value}
                  data-testid={`${testid}-option-${o.value}`}
                  style={field.key === 'brand_font_family' && o.value !== 'system' ? { fontFamily: `${o.value}, system-ui` } : undefined}
                >
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {dirty && <Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge>}
        </div>
      </div>
    );
  }
  if (field.type === 'reorder') {
    return (
      <div className="py-2" data-testid={testid}>
        <ReorderList value={Array.isArray(value) ? value : DEFAULTS.landing_section_order}
          onChange={onChange} dirty={dirty} />
      </div>
    );
  }
  if (field.type === 'tiles') {
    return (
      <div className="py-2" data-testid={testid}>
        <TilesEditor
          value={Array.isArray(value) && value.length ? value : DEFAULTS.landing_service_tiles}
          onChange={onChange}
          dirty={dirty}
        />
      </div>
    );
  }
  if (field.type === 'city_tax') {
    return (
      <div className="py-2" data-testid={testid}>
        <CityTaxEditor
          value={value && typeof value === 'object' ? value : DEFAULTS.city_default_tax_rates}
          onChange={onChange}
          dirty={dirty}
        />
      </div>
    );
  }
  if (field.type === 'custom_sections') {
    return (
      <div className="py-2" data-testid={testid}>
        <CustomSectionsEditor value={Array.isArray(value) ? value : []} onChange={onChange} dirty={dirty} />
      </div>
    );
  }
  if (field.type === 'section_layout') {
    return (
      <div className="py-2" data-testid={testid}>
        <SectionLayoutEditor value={value && typeof value === 'object' ? value : {}} onChange={onChange} dirty={dirty} />
      </div>
    );
  }
  // default: text
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
      <div>
        <div className="text-sm font-medium text-slate-800">{field.label}</div>
        <div className="text-[11px] text-slate-400">{field.key}</div>
      </div>
      <div className="sm:col-span-2 flex items-center gap-2">
        <Input value={String(value || '')} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={`${testid}-input`} />
        {dirty && <Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge>}
      </div>
    </div>
  );
};

const SECTION_LABELS = {
  service_tiles: 'Service tiles (Food, Grocery, Courier, …)',
  modes_ribbon: 'Modes ribbon (Road, Air, Sea, Rail, Bike)',
  promise_ribbon: 'Promise ribbon (Fast & Reliable, Safe & Secure, …)',
};

const CUSTOM_SECTION_KINDS = [
  { value: 'headline_banner', label: 'Headline banner', icon: Megaphone,
    defaults: () => ({ title: 'Big announcement', subtitle: 'Details go here', background_color: '#0f172a', text_color: '#ffffff', cta_label: '', cta_link: '' }) },
  { value: 'image_row', label: 'Image row', icon: ImageIcon,
    defaults: () => ({ title: '', columns: 3, images: [{ url: '', alt: '', link: '' }, { url: '', alt: '', link: '' }, { url: '', alt: '', link: '' }] }) },
  { value: 'testimonials', label: 'Testimonials', icon: MessageSquare,
    defaults: () => ({ title: 'What customers say', items: [{ quote: '', author: '', avatar: '' }] }) },
  { value: 'video_embed', label: 'Video embed', icon: Youtube,
    defaults: () => ({ title: '', video_url: '' }) },
];

const CustomSectionsEditor = ({ value, onChange, dirty }) => {
  const [expanded, setExpanded] = React.useState(null);
  const [addOpen, setAddOpen] = React.useState(false);
  const update = (idx, patch) => onChange(value.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const remove = (idx) => {
    if (!window.confirm(`Delete section "${value[idx].title || value[idx].id}"?`)) return;
    onChange(value.filter((_, i) => i !== idx));
  };
  const move = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const next = value.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const addKind = (kind) => {
    const spec = CUSTOM_SECTION_KINDS.find((k) => k.value === kind);
    if (!spec) return;
    const id = `cs_${Math.random().toString(36).slice(2, 8)}`;
    onChange([...value, { id, kind, ...spec.defaults() }]);
    setExpanded(value.length);
    setAddOpen(false);
  };

  return (
    <div className="space-y-2" data-testid="config-custom-sections-editor">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-slate-500">Section ids are auto-generated; drag them into the Layout order to place them exactly.</div>
        <div className="relative">
          <Button size="sm" onClick={() => setAddOpen((v) => !v)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="config-cs-add">
            <Plus className="h-3.5 w-3.5 mr-1" />Add section
          </Button>
          {addOpen && (
            <div className="absolute right-0 mt-1 z-10 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden w-56" data-testid="config-cs-add-menu">
              {CUSTOM_SECTION_KINDS.map((k) => {
                const Icon = k.icon;
                return (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => addKind(k.value)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
                    data-testid={`config-cs-add-${k.value}`}
                  >
                    <Icon className="h-4 w-4 text-slate-500" />{k.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {value.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-6 border border-dashed border-slate-200 rounded-lg">
          No custom sections yet. Add a headline banner, image row, testimonials block or a video embed.
        </div>
      ) : value.map((s, idx) => {
        const kindSpec = CUSTOM_SECTION_KINDS.find((k) => k.value === s.kind) || CUSTOM_SECTION_KINDS[0];
        const Icon = kindSpec.icon;
        const isOpen = expanded === idx;
        return (
          <div key={s.id} className={`bg-white border rounded-lg transition ${isOpen ? 'border-emerald-300 shadow-sm' : 'border-slate-200'}`} data-testid={`config-cs-row-${s.id}`}>
            <div className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
              <button type="button" onClick={() => setExpanded(isOpen ? null : idx)} className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`config-cs-toggle-${s.id}`}>
                <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{s.title || kindSpec.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{kindSpec.label} · id {s.id}</div>
                </div>
              </button>
              <button type="button" onClick={() => move(idx, idx - 1)} disabled={idx === 0} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Move up">
                <ArrowUp className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => move(idx, idx + 1)} disabled={idx === value.length - 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Move down">
                <ArrowDown className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => remove(idx)} className="p-1 rounded text-rose-500 hover:bg-rose-50" data-testid={`config-cs-delete-${s.id}`} aria-label="Delete section">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {isOpen && (
              <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-2">
                <CustomSectionForm section={s} onChange={(patch) => update(idx, patch)} />
              </div>
            )}
          </div>
        );
      })}
      {dirty && <div className="pt-1"><Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge></div>}
    </div>
  );
};

const CustomSectionForm = ({ section, onChange }) => {
  const set = (patch) => onChange(patch);
  if (section.kind === 'headline_banner') {
    return (
      <>
        <FieldLabel>Title</FieldLabel>
        <Input value={section.title || ''} onChange={(e) => set({ title: e.target.value })} data-testid={`cs-form-title-${section.id}`} />
        <FieldLabel>Subtitle</FieldLabel>
        <Input value={section.subtitle || ''} onChange={(e) => set({ subtitle: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <FieldLabel>Background</FieldLabel>
            <ColorField value={section.background_color} onChange={(v) => set({ background_color: v })} />
          </div>
          <div>
            <FieldLabel>Text color</FieldLabel>
            <ColorField value={section.text_color} onChange={(v) => set({ text_color: v })} />
          </div>
        </div>
        <FieldLabel>CTA label (optional)</FieldLabel>
        <Input value={section.cta_label || ''} onChange={(e) => set({ cta_label: e.target.value })} placeholder="Learn more" />
        <FieldLabel>CTA link</FieldLabel>
        <Input value={section.cta_link || ''} onChange={(e) => set({ cta_link: e.target.value })} placeholder="https://…" />
      </>
    );
  }
  if (section.kind === 'image_row') {
    const setImage = (i, patch) => set({ images: (section.images || []).map((im, ii) => (ii === i ? { ...im, ...patch } : im)) });
    const addImage = () => set({ images: [...(section.images || []), { url: '', alt: '', link: '' }] });
    const removeImage = (i) => set({ images: (section.images || []).filter((_, ii) => ii !== i) });
    return (
      <>
        <FieldLabel>Section title (optional)</FieldLabel>
        <Input value={section.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="Featured brands" />
        <FieldLabel>Columns</FieldLabel>
        <Input type="number" min="1" max="6" value={section.columns || 3} onChange={(e) => set({ columns: Number(e.target.value) })} />
        <FieldLabel>Images</FieldLabel>
        {(section.images || []).map((img, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center bg-slate-50 rounded p-2">
            <Input value={img.url} onChange={(e) => setImage(i, { url: e.target.value })} placeholder="https://image.url/photo.jpg" />
            <Input value={img.alt} onChange={(e) => setImage(i, { alt: e.target.value })} placeholder="alt text" />
            <div className="flex items-center gap-1">
              <Input value={img.link} onChange={(e) => setImage(i, { link: e.target.value })} placeholder="click link (optional)" />
              <Button size="sm" variant="outline" onClick={() => removeImage(i)} className="text-rose-600 border-rose-200"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addImage} data-testid={`cs-form-add-image-${section.id}`}><Plus className="h-3.5 w-3.5 mr-1" />Add image</Button>
      </>
    );
  }
  if (section.kind === 'testimonials') {
    const setItem = (i, patch) => set({ items: (section.items || []).map((it, ii) => (ii === i ? { ...it, ...patch } : it)) });
    const addItem = () => set({ items: [...(section.items || []), { quote: '', author: '', avatar: '' }] });
    const removeItem = (i) => set({ items: (section.items || []).filter((_, ii) => ii !== i) });
    return (
      <>
        <FieldLabel>Section title</FieldLabel>
        <Input value={section.title || ''} onChange={(e) => set({ title: e.target.value })} />
        {(section.items || []).map((it, i) => (
          <div key={i} className="bg-slate-50 rounded p-2 space-y-1.5">
            <FieldLabel>Quote #{i + 1}</FieldLabel>
            <textarea value={it.quote} onChange={(e) => setItem(i, { quote: e.target.value })} rows={2} className="w-full text-sm border border-slate-200 rounded p-2" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={it.author} onChange={(e) => setItem(i, { author: e.target.value })} placeholder="Author" />
              <div className="flex items-center gap-1">
                <Input value={it.avatar} onChange={(e) => setItem(i, { avatar: e.target.value })} placeholder="Avatar URL" />
                <Button size="sm" variant="outline" onClick={() => removeItem(i)} className="text-rose-600 border-rose-200"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" />Add testimonial</Button>
      </>
    );
  }
  if (section.kind === 'video_embed') {
    return (
      <>
        <FieldLabel>Section title (optional)</FieldLabel>
        <Input value={section.title || ''} onChange={(e) => set({ title: e.target.value })} />
        <FieldLabel>Video URL (YouTube / Vimeo / MP4)</FieldLabel>
        <Input value={section.video_url || ''} onChange={(e) => set({ video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" data-testid={`cs-form-video-${section.id}`} />
      </>
    );
  }
  return null;
};

const FieldLabel = ({ children }) => (
  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{children}</div>
);

const ColorField = ({ value, onChange }) => (
  <div className="flex items-center gap-1.5">
    <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 rounded border border-slate-200 cursor-pointer" />
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="font-mono h-9" />
  </div>
);

// -------- Per-section layout sliders --------
const LAYOUT_TARGETS = [
  { id: 'hero',            label: 'Hero block (search + tagline + mission)' },
  { id: 'service_tiles',   label: 'Service tiles grid' },
  { id: 'modes_ribbon',    label: 'Modes ribbon' },
  { id: 'promise_ribbon',  label: 'Promise ribbon' },
];

const SectionLayoutEditor = ({ value, onChange, dirty }) => {
  const set = (id, patch) => onChange({ ...(value || {}), [id]: { ...(value?.[id] || {}), ...patch } });
  const resetOne = (id) => {
    const next = { ...(value || {}) };
    delete next[id];
    onChange(next);
  };
  return (
    <div className="space-y-2" data-testid="config-section-layout-editor">
      <div className="text-[11px] text-slate-500">Drag the sliders to tune width and padding. Empty = use the built-in default.</div>
      {LAYOUT_TARGETS.map((t) => {
        const cur = value?.[t.id] || {};
        return (
          <div key={t.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`config-layout-row-${t.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-slate-800">{t.label}</div>
              {Object.keys(cur).length > 0 && (
                <button type="button" onClick={() => resetOne(t.id)} className="text-[11px] text-slate-500 hover:text-slate-800 underline">Reset this box</button>
              )}
            </div>
            <SliderRow label="Max width" min={320} max={1600} step={10} value={cur.max_width_px} onChange={(v) => set(t.id, { max_width_px: v })} unit="px" testid={`layout-width-${t.id}`} />
            <SliderRow label="Vertical padding" min={0} max={80} step={2} value={cur.padding_y} onChange={(v) => set(t.id, { padding_y: v })} unit="px" testid={`layout-py-${t.id}`} />
            <SliderRow label="Horizontal padding" min={0} max={80} step={2} value={cur.padding_x} onChange={(v) => set(t.id, { padding_x: v })} unit="px" testid={`layout-px-${t.id}`} />
          </div>
        );
      })}
      {dirty && <div className="pt-1"><Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge></div>}
    </div>
  );
};

const SliderRow = ({ label, min, max, step = 1, value, onChange, unit, testid }) => {
  const isSet = value != null && value !== '';
  return (
    <div className="grid grid-cols-3 gap-2 items-center py-1">
      <div className="text-xs text-slate-600">{label}</div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={isSet ? value : Math.round((min + max) / 2)}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-emerald-600"
        data-testid={testid}
      />
      <div className="flex items-center gap-1">
        <Input type="number" min={min} max={max} step={step} value={isSet ? value : ''} placeholder="auto" onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} className="h-8 w-20" />
        <span className="text-[11px] text-slate-500">{unit}</span>
      </div>
    </div>
  );
};

const ICON_OPTIONS = [
  { name: 'Utensils', Icon: Utensils },
  { name: 'ShoppingBasket', Icon: ShoppingBasket },
  { name: 'Leaf', Icon: Leaf },
  { name: 'Bike', Icon: Bike },
  { name: 'Package', Icon: Package },
  { name: 'Truck', Icon: Truck },
  { name: 'Building2', Icon: Building2 },
  { name: 'Coffee', Icon: Coffee },
  { name: 'Gift', Icon: Gift },
  { name: 'Heart', Icon: Heart },
  { name: 'Store', Icon: Store },
  { name: 'Wrench', Icon: Wrench },
  { name: 'Wine', Icon: Wine },
  { name: 'Home', Icon: Home },
  { name: 'Baby', Icon: Baby },
  { name: 'Book', Icon: Book },
  { name: 'Camera', Icon: Camera },
  { name: 'Music', Icon: Music },
];

const CATEGORY_OPTIONS = [
  { value: 'food', label: 'Food & Restaurants' },
  { value: 'grocery', label: 'African Grocery' },
  { value: 'farm_produce', label: 'Farm Produce' },
  { value: 'courier', label: 'Courier Services' },
  { value: 'package', label: 'Same-Day Package' },
  { value: 'logistics', label: 'Business Logistics' },
  { value: 'all', label: 'All Vendors' },
  { value: '', label: 'No category filter' },
];

const TilesEditor = ({ value, onChange, dirty }) => {
  const [expanded, setExpanded] = React.useState(null);
  const update = (idx, patch) => onChange(value.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  const remove = (idx) => {
    if (!window.confirm(`Delete tile "${value[idx].name}"? This can't be undone by clicking Reload.`)) return;
    onChange(value.filter((_, i) => i !== idx));
  };
  const add = () => {
    const id = `tile_${Math.random().toString(36).slice(2, 8)}`;
    onChange([...value, { id, name: 'New tile', icon: 'Package', color: '#0EA5E9', category: '', query: '', hidden: false }]);
    setExpanded(value.length);
  };
  const move = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const next = value.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  return (
    <div className="space-y-2" data-testid="config-tiles-editor">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-slate-500">Click a row to edit. Reorder with the arrows.</div>
        <Button size="sm" onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="config-tiles-add">
          <Plus className="h-3.5 w-3.5 mr-1" />Add tile
        </Button>
      </div>
      {value.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-6 border border-dashed border-slate-200 rounded-lg">No tiles yet. Click Add tile.</div>
      ) : value.map((t, idx) => {
        const IconComp = ICON_OPTIONS.find((o) => o.name === t.icon)?.Icon || Package;
        const isOpen = expanded === idx;
        return (
          <div key={t.id} className={`bg-white border rounded-lg transition ${t.hidden ? 'opacity-60' : ''} ${isOpen ? 'border-emerald-300 shadow-sm' : 'border-slate-200'}`} data-testid={`config-tile-row-${t.id}`}>
            <div className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : idx)}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
                data-testid={`config-tile-toggle-${t.id}`}
              >
                <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${t.color}1A`, color: t.color }}>
                  <IconComp className="h-4 w-4" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{t.name || 'Unnamed tile'}</div>
                  <div className="text-[11px] text-slate-500 truncate">{t.icon} · {t.category || 'no category'}{t.hidden ? ' · hidden' : ''}</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => move(idx, idx - 1)}
                disabled={idx === 0}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                aria-label="Move up"
              ><ArrowUp className="h-4 w-4" /></button>
              <button
                type="button"
                onClick={() => move(idx, idx + 1)}
                disabled={idx === value.length - 1}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                aria-label="Move down"
              ><ArrowDown className="h-4 w-4" /></button>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="p-1 rounded text-rose-500 hover:bg-rose-50"
                data-testid={`config-tile-delete-${t.id}`}
                aria-label="Delete tile"
              ><Trash2 className="h-4 w-4" /></button>
            </div>
            {isOpen && (
              <div className="px-3 pb-3 pt-1 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="sm:col-span-2">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Name</label>
                  <Input value={t.name} onChange={(e) => update(idx, { name: e.target.value })} data-testid={`config-tile-name-${t.id}`} />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Icon</label>
                  <select
                    value={t.icon}
                    onChange={(e) => update(idx, { icon: e.target.value })}
                    className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    data-testid={`config-tile-icon-${t.id}`}
                  >
                    {ICON_OPTIONS.map((o) => <option key={o.name} value={o.name}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={t.color || '#0EA5E9'} onChange={(e) => update(idx, { color: e.target.value })} className="h-9 w-12 rounded border border-slate-200 cursor-pointer" data-testid={`config-tile-color-${t.id}`} />
                    <Input value={t.color || ''} onChange={(e) => update(idx, { color: e.target.value })} className="font-mono h-9" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Category / link</label>
                  <select
                    value={t.category || ''}
                    onChange={(e) => update(idx, { category: e.target.value })}
                    className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    data-testid={`config-tile-category-${t.id}`}
                  >
                    {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Search query (optional)</label>
                  <Input value={t.query || ''} onChange={(e) => update(idx, { query: e.target.value })} placeholder="e.g. farm" data-testid={`config-tile-query-${t.id}`} />
                </div>
                <label className="flex items-center gap-2 sm:col-span-2 pt-1">
                  <input type="checkbox" checked={!!t.hidden} onChange={(e) => update(idx, { hidden: e.target.checked })} className="accent-slate-800" data-testid={`config-tile-hidden-${t.id}`} />
                  <span className="text-xs text-slate-600">Hide this tile from the landing page (keeps the config).</span>
                </label>
              </div>
            )}
          </div>
        );
      })}
      {dirty && <div className="pt-1"><Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge></div>}
    </div>
  );
};

const CityTaxEditor = ({ value, onChange, dirty }) => {
  const entries = Object.entries(value || {});
  const setKey = (oldKey, newKey) => {
    if (newKey === oldKey || !newKey.trim()) return;
    const next = { ...value };
    next[newKey.trim()] = next[oldKey];
    delete next[oldKey];
    onChange(next);
  };
  const setRate = (key, rate) => {
    const num = Number(rate);
    if (Number.isNaN(num)) return;
    onChange({ ...value, [key]: num });
  };
  const remove = (key) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };
  const add = () => {
    let base = 'City';
    let i = 1;
    while (Object.prototype.hasOwnProperty.call(value || {}, `${base} ${i}`)) i += 1;
    onChange({ ...(value || {}), [`${base} ${i}`]: 0 });
  };
  return (
    <div className="space-y-2" data-testid="config-city-tax-editor">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-slate-500">Rates apply only when the vendor has left <span className="font-mono">tax_rate</span> blank.</div>
        <Button size="sm" onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="config-city-tax-add">
          <Plus className="h-3.5 w-3.5 mr-1" />Add city
        </Button>
      </div>
      {entries.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-6 border border-dashed border-slate-200 rounded-lg">No city defaults yet.</div>
      ) : entries.map(([city, rate]) => (
        <div key={city} className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-center bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`config-city-tax-row-${city}`}>
          <div className="sm:col-span-3">
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">City name</label>
            <Input defaultValue={city} onBlur={(e) => setKey(city, e.target.value)} placeholder="Ottawa" data-testid={`config-city-tax-name-${city}`} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Rate (%)</label>
            <Input type="number" step="0.1" min="0" max="100" value={String(rate ?? 0)} onChange={(e) => setRate(city, e.target.value)} data-testid={`config-city-tax-rate-${city}`} />
          </div>
          <div className="pt-4">
            <Button size="sm" variant="outline" onClick={() => remove(city)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`config-city-tax-remove-${city}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}
      {dirty && <div className="pt-1"><Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge></div>}
    </div>
  );
};

const ReorderList = ({ value, onChange, dirty }) => {
  // Native HTML5 drag-and-drop — kept dep-free since we only reorder 3 items.
  // Arrow buttons remain for keyboard / touch accessibility.
  const [dragId, setDragId] = React.useState(null);
  const move = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const next = value.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const from = value.indexOf(dragId);
    const to = value.indexOf(targetId);
    if (from < 0 || to < 0) return;
    move(from, to);
    setDragId(null);
  };
  return (
    <div className="space-y-2" data-testid="config-reorder-list">
      <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
        <ArrowUpDown className="h-3 w-3" /> Drag rows to reorder, or use the arrows.
      </div>
      {value.map((id, idx) => (
        <div
          key={id}
          draggable
          onDragStart={() => onDragStart(id)}
          onDragOver={onDragOver}
          onDrop={() => onDrop(id)}
          data-testid={`config-reorder-row-${id}`}
          className={`group flex items-center gap-2 bg-white border rounded-lg px-3 py-2.5 shadow-sm transition ${dragId === id ? 'opacity-50 border-emerald-400' : 'border-slate-200 hover:border-slate-300'}`}
        >
          <GripVertical className="h-4 w-4 text-slate-400 cursor-grab active:cursor-grabbing" />
          <div className="text-[11px] font-semibold text-slate-400 tabular-nums w-4">{idx + 1}</div>
          <div className="flex-1 text-sm font-medium text-slate-800">{SECTION_LABELS[id] || id}</div>
          <button
            type="button"
            onClick={() => move(idx, idx - 1)}
            disabled={idx === 0}
            className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
            data-testid={`config-reorder-up-${id}`}
            aria-label="Move up"
          ><ArrowUp className="h-4 w-4" /></button>
          <button
            type="button"
            onClick={() => move(idx, idx + 1)}
            disabled={idx === value.length - 1}
            className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
            data-testid={`config-reorder-down-${id}`}
            aria-label="Move down"
          ><ArrowDown className="h-4 w-4" /></button>
        </div>
      ))}
      {dirty && <Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge>}
    </div>
  );
};

const HeroPreview = ({ title, tagline, mission, primary, accent, showServiceTiles, showModes, showPromise, tiles, modesTitle, tileSize, vendorHeading }) => {
  const activeTiles = Array.isArray(tiles) ? tiles.filter((t) => !t.hidden) : [];
  const sizeClass = { compact: 'p-2', default: 'p-3', roomy: 'p-4' }[tileSize] || 'p-3';
  return (
  <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-testid="config-preview-card">
    <div className="p-6 text-center" style={{ background: `linear-gradient(135deg, ${primary}f0, #0f172a)` }}>
      <img src="/shipitnow-logo.png" alt="ShipitNow" className="h-16 mx-auto mb-3" />
      <div className="text-3xl font-extrabold text-white">{title}</div>
      <div className="mt-1 text-sm font-semibold uppercase tracking-widest" style={{ color: accent }}>{tagline}</div>
      <div className="mt-3 text-sm text-slate-200 max-w-lg mx-auto">{mission}</div>
    </div>
    {showServiceTiles && activeTiles.length > 0 && (
      <div className="p-3 bg-slate-50 grid grid-cols-3 gap-2" data-testid="config-preview-tiles">
        {activeTiles.slice(0, 6).map((t) => {
          const IconComp = ICON_OPTIONS.find((o) => o.name === t.icon)?.Icon || Package;
          return (
            <div key={t.id} className={`bg-white rounded-lg ${sizeClass} text-center`}>
              <div className="h-9 w-9 mx-auto rounded-md flex items-center justify-center" style={{ backgroundColor: `${t.color}1A`, color: t.color }}>
                <IconComp className="h-4 w-4" strokeWidth={2.2} />
              </div>
              <div className="mt-1 text-[11px] font-semibold text-slate-800 truncate">{t.name}</div>
            </div>
          );
        })}
      </div>
    )}
    {showModes && (
      <div className="px-4 py-2 bg-white text-center text-[10px] tracking-[0.22em] font-bold uppercase border-t border-slate-100" style={{ color: primary }}>
        {modesTitle || 'All Modes · One Promise'}
      </div>
    )}
    {showPromise && (
      <div className="px-4 py-2 text-white text-center text-[11px] font-semibold uppercase tracking-wide"
        style={{ background: `linear-gradient(90deg, ${primary}, ${accent})` }}>
        Fast · Safe · Local · Business
      </div>
    )}
    {vendorHeading && (
      <div className="px-4 py-3 bg-white border-t border-slate-100">
        <div className="text-lg font-semibold text-slate-900">{vendorHeading}</div>
      </div>
    )}
    <div className="px-4 py-3 bg-slate-50 text-xs text-slate-600 flex items-center gap-2 flex-wrap" data-testid="config-preview-sections">
      <span>Sections visible:</span>
      <SectionChip on={showServiceTiles} label={`Service tiles (${activeTiles.length})`} />
      <SectionChip on={showModes} label="Modes ribbon" />
      <SectionChip on={showPromise} label="Promise ribbon" />
    </div>
  </div>
  );
};

const SectionChip = ({ on, label }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${on ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500 line-through'}`}>
    {on ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}{label}
  </span>
);

export default ConfigurationPage;
